import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HUB_ORIGIN = '50% 50%';

/**
 * @param {HTMLElement} section
 */
const setActiveStep = (section, stepIndex) => {
	const markers = section.querySelectorAll('.js-process-marker');
	const callouts = section.querySelectorAll('.js-process-callout');
	const steps = section.querySelectorAll('.process__step');

	markers.forEach((el) => {
		el.classList.toggle('is-active', el.dataset.processStep === stepIndex);
	});
	callouts.forEach((el) => {
		el.classList.toggle('is-active', el.dataset.processStep === stepIndex);
	});
	steps.forEach((el) => {
		const idx =
			el.querySelector('.js-process-marker')?.dataset.processStep ??
			el.querySelector('.js-process-callout')?.dataset.processStep;
		el.classList.toggle('is-active', idx === stepIndex);
	});
};

/**
 * @param {HTMLElement} section
 */
const bindStepInteractions = (section) => {
	const markers = section.querySelectorAll('.js-process-marker');
	const callouts = section.querySelectorAll('.js-process-callout');

	const activate = (stepIndex) => {
		if (stepIndex) {
			setActiveStep(section, stepIndex);
		}
	};

	markers.forEach((marker) => {
		marker.addEventListener('mouseenter', () => activate(marker.dataset.processStep));
		marker.addEventListener('focus', () => activate(marker.dataset.processStep));
		marker.addEventListener('click', () => activate(marker.dataset.processStep));
	});

	callouts.forEach((callout) => {
		callout.addEventListener('mouseenter', () => activate(callout.dataset.processStep));
		callout.addEventListener('focusin', () => activate(callout.dataset.processStep));
	});

	section.querySelectorAll('.process__step').forEach((step) => {
		step.addEventListener('mouseenter', () => {
			const idx =
				step.querySelector('.js-process-marker')?.dataset.processStep ??
				step.querySelector('.js-process-callout')?.dataset.processStep;
			activate(idx);
		});
	});
};

/**
 * @param {HTMLElement} section
 */
const bindSection = (section) => {
	const visual = section.querySelector('.js-process-visual');
	if (!visual) {
		return null;
	}

	bindStepInteractions(section);

	const orbitFills = visual.querySelectorAll('.process__orbit-fill');
	const orbits = visual.querySelectorAll('.process__orbit--outer, .process__orbit--mid');
	const innerOrbit = visual.querySelector('.process__inner-orbit');
	const hubLogo = visual.querySelector('.process__hub-logo');
	const introBits = section.querySelectorAll(
		'.process__intro .text-eyebrow, .process__headline, .process__body, .process__cta',
	);
	const callouts = section.querySelectorAll('.js-process-callout');
	const markers = section.querySelectorAll('.js-process-marker');
	const steps = section.querySelectorAll('.process__step');

	const orbitTargets = [...orbitFills, ...orbits, ...(innerOrbit ? [innerOrbit] : [])];

	gsap.set(orbitTargets, { transformOrigin: HUB_ORIGIN, scale: 0.72, autoAlpha: 0 });
	if (hubLogo) {
		gsap.set(hubLogo, { transformOrigin: HUB_ORIGIN, scale: 0.85, autoAlpha: 0 });
	}
	if (introBits.length) {
		gsap.set(introBits, { y: 20, autoAlpha: 0 });
	}
	if (steps.length) {
		gsap.set(steps, { y: 12, autoAlpha: 0 });
	} else if (callouts.length) {
		gsap.set(callouts, { y: 12, autoAlpha: 0 });
	}
	if (markers.length) {
		gsap.set(markers, { scale: 0.6, autoAlpha: 0 });
	}

	const entrance = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: 'top 78%',
			once: true,
		},
		defaults: { ease: 'power3.out' },
	});

	if (introBits.length) {
		entrance.to(introBits, { y: 0, autoAlpha: 1, duration: 0.65, stagger: 0.08 }, 0);
	}

	entrance
		.to(
			orbitFills,
			{ scale: 1, autoAlpha: 1, duration: 1, stagger: 0.08, ease: 'power2.out' },
			0.1,
		)
		.to(
			visual.querySelector('.process__orbit--outer'),
			{ scale: 1, autoAlpha: 1, duration: 0.95, ease: 'back.out(1.35)' },
			0.16,
		)
		.to(
			visual.querySelector('.process__orbit--mid'),
			{ scale: 1, autoAlpha: 1, duration: 0.9, ease: 'back.out(1.4)' },
			0.24,
		)
		.to(
			innerOrbit,
			{ scale: 1, autoAlpha: 1, duration: 0.88, ease: 'back.out(1.5)' },
			0.32,
		);

	if (hubLogo) {
		entrance.to(hubLogo, { scale: 1, autoAlpha: 1, duration: 0.75, ease: 'power2.out' }, 0.38);
	}

	if (markers.length) {
		entrance.to(markers, { scale: 1, autoAlpha: 1, duration: 0.5, stagger: 0.08, ease: 'back.out(2)' }, 0.4);
	}

	if (steps.length) {
		entrance.to(steps, { y: 0, autoAlpha: 1, duration: 0.55, stagger: 0.07 }, 0.48);
	} else if (callouts.length) {
		entrance.to(callouts, { y: 0, autoAlpha: 1, duration: 0.55, stagger: 0.07 }, 0.48);
	}

	const animated = [
		...orbitTargets,
		...(hubLogo ? [hubLogo] : []),
		...introBits,
		...callouts,
		...markers,
		...steps,
	];

	return () => {
		entrance.scrollTrigger?.kill();
		gsap.killTweensOf(animated);
		gsap.set(animated, { clearProps: 'all' });
	};
};

/**
 * @param {HTMLElement} section
 */
const bindSectionReduced = (section) => {
	const targets = section.querySelectorAll('.process__intro, .process__diagram');
	if (!targets.length) {
		return null;
	}

	gsap.set(targets, { autoAlpha: 0 });

	const tween = gsap.to(targets, {
		autoAlpha: 1,
		duration: 0.5,
		stagger: 0.06,
		ease: 'power2.out',
		scrollTrigger: {
			trigger: section,
			start: 'top 85%',
			once: true,
		},
	});

	return () => {
		tween.scrollTrigger?.kill();
		gsap.killTweensOf(targets);
		gsap.set(targets, { clearProps: 'all' });
	};
};

export const initProcessHubAnimation = () => {
	const sections = document.querySelectorAll('.js-process-section');
	if (!sections.length) {
		return;
	}

	const cleanups = [];

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (min-width: 640px)', () => {
		sections.forEach((section) => {
			const cleanup = bindSection(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});

	gsap.matchMedia().add('(prefers-reduced-motion: reduce)', () => {
		sections.forEach((section) => {
			const cleanup = bindSectionReduced(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});
};
